import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
  TextField,
} from '@mui/material';
import {
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Preview as PreviewIcon,
  Refresh as RefreshIcon,
  Web as WebIcon,
  SmartToy as BotIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities } from '../store/slices/entitySlice';
import { showSuccess, showError } from '../store/slices/toastSlice';
import { chatApi } from '../services/chatApi';
import { SnippetRequest, SnippetResponse } from '../types';
import { useLocation, useNavigate } from 'react-router-dom';

const SampleWebsitePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { entities, isLoading: entitiesLoading } = useAppSelector((state) => state.entities);

  // Get data from navigation state (passed from Snippet Generator)
  const passedData = location.state as { snippet: SnippetResponse; customizations: any } | null;

  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [snippet, setSnippet] = useState<SnippetResponse | null>(passedData?.snippet || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWidget, setShowWidget] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true); // Start in fullscreen
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ id: string, type: 'user' | 'bot', content: string, timestamp: Date }>>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [widgetConfig, setWidgetConfig] = useState(passedData?.customizations || {
    theme: 'modern',
    position: 'bottom-right',
    primaryColor: '#FF7316',
    showBranding: true,
    autoOpen: false,
    welcomeMessage: 'Hello! How can I help you today?',
  });

  useEffect(() => {
    dispatch(fetchEntities());

    // Check for persisted snippet data in localStorage
    const persistedSnippet = localStorage.getItem('asknow-demo-snippet');
    const persistedConfig = localStorage.getItem('asknow-demo-config');

    if (passedData?.snippet) {
      // New snippet from navigation - replace old one
      setSnippet(passedData.snippet);
      setWidgetConfig(passedData.customizations);
      setSelectedEntityId(passedData.snippet.entity_id);
      setShowWidget(true);

      // Persist to localStorage
      localStorage.setItem('asknow-demo-snippet', JSON.stringify(passedData.snippet));
      localStorage.setItem('asknow-demo-config', JSON.stringify(passedData.customizations));

      // Auto-load the widget
      setTimeout(() => {
        loadWidgetInIframe();
      }, 500);
    } else if (persistedSnippet && persistedConfig) {
      // Load persisted snippet after reload
      try {
        const snippetData = JSON.parse(persistedSnippet);
        const configData = JSON.parse(persistedConfig);

        setSnippet(snippetData);
        setWidgetConfig(configData);
        setSelectedEntityId(snippetData.entity_id);
        setShowWidget(true);

        // Auto-load the widget
        setTimeout(() => {
          loadWidgetInIframe();
        }, 500);
      } catch (error) {
        console.error('Error loading persisted snippet:', error);
        localStorage.removeItem('asknow-demo-snippet');
        localStorage.removeItem('asknow-demo-config');
      }
    }
  }, [dispatch, passedData]);

  const handleGenerateSnippet = async () => {
    if (!selectedEntityId) {
      setError('Please select an entity first');
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      const request: SnippetRequest = {
        entity_id: selectedEntityId,
      };

      const response = await chatApi.generateSnippet(request);
      setSnippet(response);

      // Persist new snippet to localStorage
      localStorage.setItem('asknow-demo-snippet', JSON.stringify(response));
      localStorage.setItem('asknow-demo-config', JSON.stringify(widgetConfig));

      dispatch(showSuccess({
        title: 'Snippet Generated',
        message: 'JavaScript snippet generated successfully',
        duration: 3000,
      }));
    } catch (error: any) {
      setError(error.message);
      dispatch(showError({
        title: 'Generation Failed',
        message: error.message,
        duration: 5000,
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(showSuccess({
        title: 'Copied!',
        message: `${type} copied to clipboard`,
        duration: 2000,
      }));
    } catch (error) {
      dispatch(showError({
        title: 'Copy Failed',
        message: 'Failed to copy to clipboard',
        duration: 3000,
      }));
    }
  };

  const selectedEntity = entities.find(e => e.entity_id === selectedEntityId);

  const generateCustomSnippet = () => {
    if (!snippet) return '';

    return `<!-- AskNow Chatbot Widget - Generated from Backend -->
<div id="asknow-chatbot-${snippet.entity_id}"></div>
<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://cdn.asknow.com/widget.js';
    script.onload = function() {
      AskNowWidget.init({
        entityId: '${snippet.entity_id}',
        apiKey: '${snippet.api_key}',
        theme: '${widgetConfig.theme}',
        position: '${widgetConfig.position}',
        primaryColor: '${widgetConfig.primaryColor}',
        showBranding: ${widgetConfig.showBranding},
        autoOpen: ${widgetConfig.autoOpen},
        welcomeMessage: '${widgetConfig.welcomeMessage}',
        // Additional customization options
        borderRadius: '8px',
        fontSize: '14px',
        zIndex: 9999
      });
    };
    document.head.appendChild(script);
  })();
</script>`.trim();
  };

  const generateFullHTMLPage = () => {
    if (!snippet) return '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sample Website with AskNow Widget</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            min-height: 100vh;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            background: white;
            padding: 30px;
            border-radius: 15px;
            margin-bottom: 30px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .content-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
        }
        .card {
            background: white;
            padding: 25px;
            border-radius: 15px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .feature-list {
            list-style: none;
            padding: 0;
        }
        .feature-list li {
            padding: 8px 0;
            border-bottom: 1px solid #eee;
        }
        .feature-list li:before {
            content: "✓ ";
            color: #4CAF50;
            font-weight: bold;
        }
        .demo-notice {
            background: #e3f2fd;
            padding: 15px;
            border-radius: 10px;
            margin: 20px 0;
            border-left: 4px solid #2196F3;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Welcome to Our Sample Website</h1>
            <p>This is how your website would look with the AskNow chatbot widget integrated. 
            The widget appears as a floating button and provides instant AI-powered assistance to your visitors.</p>
        </div>

        <div class="content-grid">
            <div class="card">
                <h3>🚀 Features</h3>
                <p>Our AI chatbot can help visitors with:</p>
                <ul class="feature-list">
                    <li>Product information</li>
                    <li>Support questions</li>
                    <li>Order status</li>
                    <li>General inquiries</li>
                </ul>
            </div>

            <div class="card">
                <h3>💡 Benefits</h3>
                <p>The AskNow widget provides:</p>
                <ul class="feature-list">
                    <li>24/7 customer support</li>
                    <li>Instant responses</li>
                    <li>Reduced support workload</li>
                    <li>Better user experience</li>
                </ul>
            </div>
        </div>

        <div class="demo-notice">
            <strong>🤖 AskNow Widget Active</strong><br>
            Entity: ${selectedEntity?.name || 'Demo Entity'}<br>
            Theme: ${widgetConfig.theme}<br>
            Position: ${widgetConfig.position}
        </div>
    </div>

    ${generateCustomSnippet()}
</body>
</html>`.trim();
  };

  const loadWidgetInIframe = () => {
    if (!snippet) return;

    const iframe = iframeRef.current;
    if (iframe) {
      const htmlContent = generateFullHTMLPage();
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      iframe.src = url;
      setWidgetLoaded(true);
    }
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
    setTimeout(() => {
      loadWidgetInIframe();
    }, 100);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    setWidgetLoaded(false);
    if (iframeRef.current) {
      iframeRef.current.src = '';
    }
  };

  const handleChatClick = () => {
    if (snippet) {
      setShowChat(!showChat);
    }
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !snippet || isChatLoading) return;

    const userMessage = chatMessage.trim();
    setChatMessage('');

    // Add user message
    const newUserMessage = {
      id: Date.now().toString(),
      type: 'user' as const,
      content: userMessage,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, newUserMessage]);
    setIsChatLoading(true);

    try {
      // Send message to backend
      const response = await chatApi.sendMessage({
        message: userMessage,
        entity_id: snippet.entity_id
      });

      // Add bot response
      const botMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot' as const,
        content: response.response,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, botMessage]);
    } catch (error: any) {
      // Add error message
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot' as const,
        content: `Error: ${error.message}`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const goBack = () => {
    navigate('/admin/snippets');
  };

  // Fullscreen demo view
  if (isFullscreen) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fullscreen Header */}
        <AppBar position="static" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
          <Toolbar>
            <IconButton onClick={goBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              🤖 Chatbot Demo - {selectedEntity?.name || 'Live Preview'}
            </Typography>
            <Tooltip title="Minimize">
              <IconButton onClick={closeFullscreen}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Fullscreen Content */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {snippet && showWidget ? (
            <>
              <iframe
                ref={iframeRef}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="Working AskNow Widget Demo"
                onLoad={() => setWidgetLoaded(true)}
              />

              {/* Floating Chat Icon */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  backgroundColor: widgetConfig.primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  zIndex: 10000,
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 30px rgba(0,0,0,0.4)',
                  },
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                    '50%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 10px rgba(255,115,22,0.1)' },
                    '100%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                  }
                }}
                onClick={handleChatClick}
                title="Click to open chat"
              >
                💬
              </Box>

              {/* Chat Interface */}
              {showChat && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    width: 350,
                    height: 500,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    zIndex: 10001,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  {/* Chat Header */}
                  <Box sx={{
                    p: 2,
                    backgroundColor: widgetConfig.primaryColor,
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="h6">
                      AskNow Chat
                    </Typography>
                    <IconButton
                      onClick={() => setShowChat(false)}
                      sx={{ color: 'white' }}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  {/* Chat Messages */}
                  <Box sx={{
                    flex: 1,
                    p: 2,
                    overflowY: 'auto',
                    backgroundColor: '#f5f5f5'
                  }}>
                    {chatMessages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        Start a conversation with the AI assistant!
                      </Typography>
                    ) : (
                      chatMessages.map((msg) => (
                        <Box
                          key={msg.id}
                          sx={{
                            mb: 2,
                            display: 'flex',
                            justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <Paper
                            sx={{
                              p: 2,
                              maxWidth: '80%',
                              backgroundColor: msg.type === 'user' ? widgetConfig.primaryColor : 'white',
                              color: msg.type === 'user' ? 'white' : '#333333',
                              border: msg.type === 'bot' ? '1px solid #e0e0e0' : 'none'
                            }}
                          >
                            {msg.type === 'bot' ? (
                              <Box sx={{
                                '& p': { m: 0, mb: 1, color: '#333333', fontSize: '0.875rem' },
                                '& strong': { fontWeight: 600 },
                                '& ul': { pl: 2, mb: 1 },
                                '& li': { mb: 0.5, color: '#333333' },
                                '& h1, & h2, & h3': { fontSize: '1rem', fontWeight: 700, mt: 1, mb: 0.5, color: '#000' }
                              }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {msg.content}
                                </ReactMarkdown>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'white' }}>
                                {msg.content}
                              </Typography>
                            )}

                            <Typography
                              variant="caption"
                              sx={{
                                opacity: msg.type === 'user' ? 0.8 : 0.6,
                                fontSize: '0.7rem',
                                color: msg.type === 'user' ? 'white' : '#666666'
                              }}
                            >
                              {msg.timestamp.toLocaleTimeString()}
                            </Typography>
                          </Paper>
                        </Box>
                      ))
                    )}
                    {isChatLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <Paper sx={{ p: 2, backgroundColor: 'white', border: '1px solid #e0e0e0' }}>
                          <CircularProgress size={16} sx={{ mr: 1, color: widgetConfig.primaryColor }} />
                          <Typography variant="body2" component="span" sx={{ color: '#333333' }}>
                            AI is thinking...
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>

                  {/* Chat Input */}
                  <Box sx={{ p: 2, backgroundColor: 'white', borderTop: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your message..."
                        value={chatMessage}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setChatMessage(e.target.value)}
                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isChatLoading}
                        sx={{
                          '& .MuiInputBase-input': {
                            color: '#000000',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#666666',
                            opacity: 1
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleSendMessage}
                        disabled={!chatMessage.trim() || isChatLoading}
                        sx={{ backgroundColor: widgetConfig.primaryColor }}
                      >
                        <SendIcon />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2
            }}>
              <BotIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
              <Typography variant="h5" color="text.secondary">
                No snippet loaded
              </Typography>
              <Button
                variant="contained"
                onClick={goBack}
                startIcon={<ArrowBackIcon />}
              >
                Go Back to Snippet Generator
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FF7316, #1ce9e0)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            🌐 Chatbot Demo
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
            See how AskNow chatbot widget looks and works on a real website
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            This demo shows your generated snippet code in action on a sample website.
            Perfect for demonstrating to clients and students how the chatbot integrates into their websites.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Configuration Panel */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: 'fit-content', position: 'sticky', top: 20 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon color="primary" />
                  Widget Configuration
                </Typography>

                {/* Entity Selection */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Entity</InputLabel>
                  <Select
                    value={selectedEntityId}
                    onChange={(e) => setSelectedEntityId(e.target.value)}
                    label="Select Entity"
                    disabled={entitiesLoading}
                  >
                    {entities.map((entity) => (
                      <MenuItem key={entity.entity_id} value={entity.entity_id}>
                        {entity.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedEntity && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Entity:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {selectedEntity.name}
                    </Typography>
                    {selectedEntity.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Description:</strong> {selectedEntity.description}
                      </Typography>
                    )}
                    {selectedEntity.processed_data && (
                      <Chip
                        label="AI Ready"
                        color="success"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                )}

                {/* Widget Customization */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Widget Appearance:
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Theme</InputLabel>
                  <Select
                    value={widgetConfig.theme}
                    onChange={(e) => setWidgetConfig((prev: any) => ({ ...prev, theme: e.target.value }))}
                    label="Theme"
                  >
                    <MenuItem value="modern">Modern</MenuItem>
                    <MenuItem value="classic">Classic</MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={widgetConfig.position}
                    onChange={(e) => setWidgetConfig((prev: any) => ({ ...prev, position: e.target.value }))}
                    label="Position"
                  >
                    <MenuItem value="bottom-right">Bottom Right</MenuItem>
                    <MenuItem value="bottom-left">Bottom Left</MenuItem>
                    <MenuItem value="top-right">Top Right</MenuItem>
                    <MenuItem value="top-left">Top Left</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Primary Color</InputLabel>
                  <Select
                    value={widgetConfig.primaryColor}
                    onChange={(e) => setWidgetConfig((prev: any) => ({ ...prev, primaryColor: e.target.value }))}
                    label="Primary Color"
                  >
                    <MenuItem value="#FF7316">Orange (#FF7316)</MenuItem>
                    <MenuItem value="#1976d2">Blue (#1976d2)</MenuItem>
                    <MenuItem value="#388e3c">Green (#388e3c)</MenuItem>
                    <MenuItem value="#d32f2f">Red (#d32f2f)</MenuItem>
                    <MenuItem value="#7b1fa2">Purple (#7b1fa2)</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetConfig.showBranding}
                      onChange={(e) => setWidgetConfig((prev: any) => ({ ...prev, showBranding: e.target.checked }))}
                    />
                  }
                  label="Show AskNow Branding"
                  sx={{ mb: 1 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={widgetConfig.autoOpen}
                      onChange={(e) => setWidgetConfig((prev: any) => ({ ...prev, autoOpen: e.target.checked }))}
                    />
                  }
                  label="Auto Open Widget"
                />

                {/* Generate Button */}
                <Button
                  variant="contained"
                  onClick={handleGenerateSnippet}
                  disabled={!selectedEntityId || isGenerating}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <CodeIcon />}
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  {isGenerating ? 'Generating...' : 'Generate Snippet'}
                </Button>

                {snippet && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Generated Snippet:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copy HTML Snippet">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(snippet.snippet_html, 'HTML snippet')}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy Customized Snippet">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(generateCustomSnippet(), 'Customized snippet')}
                        >
                          <CodeIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sample Website Preview */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <WebIcon color="primary" />
                  <Typography variant="h6">
                    Sample Website with AskNow Widget
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {snippet && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowWidget(!showWidget)}
                        startIcon={<BotIcon />}
                      >
                        {showWidget ? 'Hide Widget' : 'Show Widget'}
                      </Button>
                    )}
                    {snippet && (
                      <Tooltip title="Open in Fullscreen">
                        <IconButton
                          onClick={openFullscreen}
                          color="primary"
                          size="small"
                        >
                          <FullscreenIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                {/* Sample Website Content */}
                <Paper
                  elevation={3}
                  sx={{
                    minHeight: '600px',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    p: 4
                  }}
                >
                  {/* Iframe for working widget */}
                  {showWidget && snippet && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1000,
                        pointerEvents: 'none'
                      }}
                    >
                      <iframe
                        ref={iframeRef}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          pointerEvents: 'auto'
                        }}
                        title="Working AskNow Widget"
                        onLoad={() => setWidgetLoaded(true)}
                      />

                      {/* Floating Chat Icon */}
                      <Box
                        sx={{
                          position: 'absolute',
                          [widgetConfig.position.includes('bottom') ? 'bottom' : 'top']: 20,
                          [widgetConfig.position.includes('right') ? 'right' : 'left']: 20,
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          backgroundColor: widgetConfig.primaryColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '24px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          zIndex: 1001,
                          pointerEvents: 'auto',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 6px 30px rgba(0,0,0,0.4)',
                          },
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                            '50%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 10px rgba(255,115,22,0.1)' },
                            '100%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                          }
                        }}
                        onClick={handleChatClick}
                        title="Click to open chat"
                      >
                        💬
                      </Box>
                    </Box>
                  )}
                  {/* Sample Website Header */}
                  <Box sx={{
                    bgcolor: 'white',
                    p: 3,
                    borderRadius: 2,
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}>
                    <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 600 }}>
                      Welcome to Our Sample Website
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      This is how your website would look with the AskNow chatbot widget integrated.
                      The widget appears as a floating button and provides instant AI-powered assistance to your visitors.
                    </Typography>
                  </Box>

                  {/* Sample Content Sections */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            🚀 Features
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Our AI chatbot can help visitors with:
                          </Typography>
                          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Product information
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Support questions
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Order status
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              General inquiries
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            💡 Benefits
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            The AskNow widget provides:
                          </Typography>
                          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                            <Typography component="li" variant="body2" color="text.secondary">
                              24/7 customer support
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Instant responses
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Reduced support workload
                            </Typography>
                            <Typography component="li" variant="body2" color="text.secondary">
                              Better user experience
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Fallback Mock Widget (when iframe not loaded) */}
                  {snippet && showWidget && !widgetLoaded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        [widgetConfig.position.includes('bottom') ? 'bottom' : 'top']: 20,
                        [widgetConfig.position.includes('right') ? 'right' : 'left']: 20,
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: widgetConfig.primaryColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '24px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 6px 30px rgba(0,0,0,0.4)',
                        },
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                          '50%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 10px rgba(255,115,22,0.1)' },
                          '100%': { boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },
                        }
                      }}
                    >
                      💬
                    </Box>
                  )}

                  {/* Widget Info */}
                  {snippet && (
                    <Box sx={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      p: 2,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        🤖 AskNow Widget Active
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Entity: {selectedEntity?.name}<br />
                        Theme: {widgetConfig.theme}<br />
                        Position: {widgetConfig.position}
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Instructions */}
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Demo Instructions:
                  </Typography>
                  <Typography variant="body2">
                    1. Select an entity from the dropdown<br />
                    2. Customize the widget appearance<br />
                    3. Click "Generate Snippet" to create real code<br />
                    4. Click "Show Widget" to see the chatbot button<br />
                    5. The widget uses your actual backend-generated snippet code
                  </Typography>
                </Alert>

                {/* Generated Code Display */}
                {snippet && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Generated Snippet Code (From Backend):
                    </Typography>
                    <Paper elevation={0} sx={{ bgcolor: 'grey.900', color: 'white', p: 2, borderRadius: 1 }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.5 }}>
                        {generateCustomSnippet()}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default SampleWebsitePage;
